import React, { useEffect, useState } from "react";
import styles from "./EditProfileInfo.module.css";

import { fetchUserPersonal, updateUserPersonal } from "../../../Api/Backend/User";
import { useAuth } from "../../../Context/AuthContext";
import PasswordPopup from "./PasswordPopup/PasswordPopup";

const EditProfileInfo = ({ onCancel }) => {
  const auth = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [originalUserInfo, setOriginalUserInfo] = useState(null);

  const [isSending, setIsSending] = useState(false);
  const [pwdPopupOpen, setPwdPopupOpen] = useState(false);

  const genders = ["남", "여"];
  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const handleFetchUserPersonal = async () => {
    await fetchUserPersonal({
      auth: auth,
      onSuccess: (data) => {
        if (data.birthDate) {
          const [year, month, day] = data.birthDate.split("-");
          const formatted = {
            ...data,
            birthYear: year,
            birthMonth: month,
            birthDay: day,
          };
          setUserInfo(formatted);
          setOriginalUserInfo(formatted);
        } else {
          setUserInfo(data);
          setOriginalUserInfo(data);
        }
      },
      onError: (error) => {
        console.error("사용자 정보 조회 중 오류가 발생했습니다: ", error?.message || error);
        alert("사용자 정보 조회 중 오류가 발생했습니다: " + (error?.message || error))
      }
    })
  };

  const handleUpdateUserPersonal = async () => {
    const payload = {};
    ["name", "birthDate", "gender", "phoneNumber"].forEach((field) => {
      if (userInfo[field] !== originalUserInfo[field]) {
        payload[field] = userInfo[field];
      }
    });

    setIsSending(true);

    await updateUserPersonal({
      auth: auth,
      payload: payload,
      onSuccess: (data) => {
        console.log("Updated user data: ", data);
        alert("사용자 정보가 성공적으로 업데이트되었습니다!");
        handleFetchUserPersonal();
        setIsSending(false);
      },
      onError: (error) => {
        console.error("사용자 정보 업데이트 실패: ", error);
        alert("사용자 정보 업데이트에 실패했습니다: " + (error?.message || error));
        setIsSending(false);
      }
    });
  };

  const prepBirthDate = (year, month, day) => {
    if (year && month != null && day != null) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${year}-${pad(month)}-${pad(day)}`;
    }
    return null;
  };

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;

    setUserInfo((prev) => {
      const updated = { ...prev, [name]: value };

      if (["birthYear", "birthMonth", "birthDay"].includes(name)) {
        updated.birthDate = prepBirthDate(
          updated.birthYear,
          updated.birthMonth,
          updated.birthDay
        );
      }

      return updated;
    });
  };

  useEffect(() => {
    handleFetchUserPersonal();
  }, []);

  return (
    <div className={styles.editProfile}>
      <h2 className={styles.sectionTitle}>회원정보 수정</h2>
      <div className={styles.infoForm}>

        <div className={styles.formRow}>
          <label>이메일주소</label>
          <input
            type="email"
            placeholder="-"
            value={userInfo?.email || ""}
            disabled
          />
        </div>

        <div className={styles.formRow}>
          <label>이름</label>
          <input
            type="text"
            placeholder="-"
            name="name"
            value={userInfo?.name || ""}
            onChange={handleUserInfoChange}
          />
        </div>

        <div className={styles.formRow}>
          <label>생년월일</label>
          <div className={styles.birthdate}>
            <input
              type="number"
              name="birthYear"
              value={userInfo?.birthYear || ""}
              onChange={handleUserInfoChange}
              onBlur={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  const clamped = Math.max(1900, Math.min(val, currentYear));
                  if (val !== clamped) {
                    handleUserInfoChange({ target: { name: "birthYear", value: clamped } });
                  }
                }
              }}
              min={1900}
              max={currentYear}
              placeholder="년도"
            />
            <select
              name="birthMonth"
              value={userInfo?.birthMonth || ""}
              onChange={handleUserInfoChange}
            >
              <option value="" disabled>월</option>
              {months.map((month) => (
                <option key={month} value={String(month).padStart(2, "0")}>
                  {month}
                </option>
              ))}
            </select>
            <select
              name="birthDay"
              value={userInfo?.birthDay || ""}
              onChange={handleUserInfoChange}
            >
              <option value="" disabled>일</option>
              {days.map((day) => (
                <option key={day} value={String(day).padStart(2, "0")}>
                  {day}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formRow}>
          <label>성별</label>
          <select
            name="gender"
            value={userInfo?.gender || ""}
            onChange={handleUserInfoChange}
          >
            <option value="" disabled>-</option>
            {genders.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formRow}>
          <label>휴대폰번호</label>
          <input
            type="number"
            placeholder="-"
            name="phoneNumber"
            value={userInfo?.phoneNumber || ""}
            onChange={handleUserInfoChange}
            disabled={userInfo?.authMethod === "LOCAL"} // Mobile phone cannot be changed for LOCAL accounts
          />
        </div>

        <div className={styles.formRow}>
          <label>비밀번호</label>
          <button
            className={styles.passwordChangeButton}
            type="button"
            onClick={() => setPwdPopupOpen(true)}
          >
            변경
          </button>
        </div>
      </div>

      <div className={styles.formActions}>
        <button
          className={styles.secondaryButton}
          onClick={!isSending && !pwdPopupOpen ? onCancel : undefined}
          disabled={isSending || pwdPopupOpen}
        >
          취소
        </button>
        <button
          className={styles.primaryButton}
          onClick={!isSending && !pwdPopupOpen ? handleUpdateUserPersonal : undefined}
          disabled={isSending || pwdPopupOpen}
        >
          저장
        </button>
      </div>

      {pwdPopupOpen &&
        <PasswordPopup auth={auth} onClose={() => setPwdPopupOpen(false)} />
      }
    </div>
  );
};

export default EditProfileInfo;
